import React, { FC } from 'react';
import styled, { keyframes } from 'styled-components';

/**
 * Inline "…with AI" text link: sparkles + label, both painted with the AI Studio purple gradient
 * and animated the same way as the AIGradientBanner title. Use for lightweight AI entry points
 * (e.g. "generate options with AI", "use AI to create your product").
 */

/* Same animated purple gradient as the AIGradientBanner title. */
const gradientShift = keyframes`
    0%   { background-position: 0% center; }
    50%  { background-position: 100% center; }
    100% { background-position: 0% center; }
`;

/** auto_awesome sparkle, filled and stroked with the moving AI Studio purple gradient. */
const GradientSparkles: FC = () => (
    <svg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none'>
        <g fill='url(#ai_link_grad)' stroke='url(#ai_link_grad)' strokeWidth='1.5' strokeLinejoin='round'>
            <polygon points='11.5,9.5 9,4 6.5,9.5 1,12 6.5,14.5 9,20 11.5,14.5 17,12' />
            <polygon points='19,9 20.25,6.25 23,5 20.25,3.75 19,1 17.75,3.75 15,5 17.75,6.25' />
            <polygon points='19,15 17.75,17.75 15,19 17.75,20.25 19,23 20.25,20.25 23,19 20.25,17.75' />
        </g>
        <defs>
            <linearGradient id='ai_link_grad' x1='0' y1='0' x2='24' y2='0' gradientUnits='userSpaceOnUse' spreadMethod='reflect'>
                <stop offset='0' stopColor='#7c3aed' />
                <stop offset='0.5' stopColor='#a855f7' />
                <stop offset='1' stopColor='#ec4899' />
                <animateTransform attributeName='gradientTransform' type='translate' from='-24 0' to='24 0' dur='8s' repeatCount='indefinite' />
            </linearGradient>
        </defs>
    </svg>
);

const Root = styled.span<{ $disabled?: boolean }>`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    cursor: ${(props) => (props.$disabled ? 'not-allowed' : 'pointer')};
    opacity: ${(props) => (props.$disabled ? 0.5 : 1)};
    transition: opacity 0.2s;

    svg {
        flex-shrink: 0;
    }

    &:hover {
        opacity: ${(props) => (props.$disabled ? 0.5 : 0.8)};
    }
`;

const Label = styled.span`
    /* inline-block + right padding stop background-clip:text from clipping the last glyph. */
    display: inline-block;
    padding-right: 2px;
    /* Darker stops than the banner: this link sits on white, so it needs more contrast to stay legible. */
    background: linear-gradient(90deg, #6d28d9, #9333ea, #db2777, #9333ea, #6d28d9);
    background-size: 300% auto;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    animation: ${gradientShift} 8s ease infinite;

    @media (prefers-reduced-motion: reduce) {
        animation: none;
    }
`;

interface AIGradientLinkProps {
    children: React.ReactNode;
    onClick?: () => void;
    disabled?: boolean;
    /** Hide the leading sparkle glyph and show gradient text only. */
    noIcon?: boolean;
    className?: string;
}

export const AIGradientLink: FC<AIGradientLinkProps> = ({ children, onClick, disabled, noIcon, className }) => {
    return (
        <Root
            className={className}
            role='button'
            tabIndex={disabled ? -1 : 0}
            aria-disabled={disabled || undefined}
            $disabled={disabled}
            onClick={disabled ? undefined : onClick}
            onKeyDown={(e) => {
                if (disabled) return;
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onClick?.();
                }
            }}
        >
            {!noIcon && <GradientSparkles />}
            <Label>{children}</Label>
        </Root>
    );
};
